import { MatrizInline, MatrizSkeleton } from '@/components/MatrizInline';
import { SnapshotsView } from '@/components/SnapshotsView';
import { AsesoriaHistorialTable } from '@/components/ui/AsesoriaHistorialTable';
import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import { IndicadoresImpacto } from '@/components/ui/IndicadoresImpacto';
import { ProgresoEmpresa } from '@/components/ui/ProgresoEmpresa';
import { CompanyIndicatorsCharts, EmpresaIndicadoresPanel } from './AdminIndicadoresPage';
import { useDashboardAsesores, useDashboardEmpresa, useDashboardEmpresas, useEvaluacionActual, useProyeccionCumplimiento } from '@/hooks';
import { useAdminSecretPanel } from '@/hooks/useAdminSecretPanel';
import { useImpersonation } from '@/hooks/useImpersonation';
import { dashboardService } from '@/services';
import type { ProyeccionCapa } from '@/services/dashboard.service';
import type { DashboardEmpresa, DashboardEmpresaNTC } from '@/types/types';
import { HorasResumenCard, computeResumenHoras } from '@/components/empresa/HorasResumenCard';
import {
  Building2,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  FileAudio,
  FileText,
  LayoutList,
  LogIn,
  Maximize,
  Search,
  Link2,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { CLAVE_FILTROS_EMPRESAS, rutaListaEmpresas } from '@/utils/empresasFiltros';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';

// ─── Empresa list column definitions ────────────────

type EmpresaColKey = 'nombre' | 'nit' | 'ntc' | 'asesor' | 'cumplimiento' | 'horas' | 'asesoria';

const EMPRESA_COLS: ColDef<EmpresaColKey>[] = [
  { key: 'nombre', label: 'Empresa', defaultVisible: true },
  { key: 'nit', label: 'NIT', defaultVisible: true },
  { key: 'ntc', label: 'Norma / Cumpl.', defaultVisible: false },
  { key: 'asesor', label: 'Asesor', defaultVisible: true },
  { key: 'cumplimiento', label: 'Cumpl. Gral', defaultVisible: true },
  { key: 'horas', label: 'Cumpl. Horas', defaultVisible: true },
  { key: 'asesoria', label: 'Estado', defaultVisible: true },
];

// ─── Helpers ────────────────────────────────────────

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function formatDate(d?: string | null) {
  if (!d) return 'N/A';
  try {
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return 'N/A';
  }
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function formatDateTime(iso: string) {
  try {
    const date = new Date(iso);
    return date.toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function ProgBar({ value, color, fill, decimals = 0 }: { value: number; color: string; fill?: boolean; decimals?: number }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${fill ? 'w-full' : ''}`}>
      <span className={`admin-prog-bar bg-white border-black border ${fill ? 'flex-1' : ''}`}>
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </span>
      <span className="text-[12px] font-medium">{value.toFixed(decimals)}%</span>
    </span>
  );
}

function pColor(v: number): string {
  if (v >= 70) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

function AudioItem({ audio }: { audio: any }) {
  const googleId = audio.googleId || audio.adjunteDelAudio;
  const duracion = audio.duracion || audio.duracionSeg || 0;

  return (
    <a
      href={`https://drive.google.com/file/d/${googleId}/preview`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 py-3 px-3 rounded-lg transition-colors"
      style={{ borderBottom: '1px solid var(--admin-border)' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--admin-purple-bg)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--admin-purple-bg)' }}>
        <FileAudio className="w-4 h-4" style={{ color: 'var(--admin-purple)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate" style={{ color: 'var(--admin-text)' }}>
          ID: {audio.audioId}
        </div>
        <div className="text-[11px]" style={{ color: 'var(--admin-text3)' }}>
          {formatDateTime(audio.fechaGrabacion)}
        </div>
      </div>
      <span className="admin-chip text-[10px]" style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text2)' }}>
        {formatDuration(duracion)}
      </span>
    </a>
  );
}

function EvidenciaItem({
  evidencia,
}: {
  evidencia: { nombre: string; descripcion: string | null; fechaAdicion: string | Date; url: string };
}) {
  return (
    <a
      href={evidencia.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 py-3 px-3 rounded-lg transition-colors"
      style={{ borderBottom: '1px solid var(--admin-border)' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--admin-green-bg)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--admin-green-bg)' }}>
        <FileText className="w-4 h-4" style={{ color: 'var(--admin-green)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate" style={{ color: 'var(--admin-text)' }}>
          {evidencia.nombre}
        </div>
        {evidencia.descripcion && (
          <div className="text-[11px] line-clamp-1" style={{ color: 'var(--admin-text3)' }}>
            {evidencia.descripcion}
          </div>
        )}
        <div className="text-[10px]" style={{ color: 'var(--admin-text3)' }}>
          {formatDateTime(evidencia.fechaAdicion instanceof Date ? evidencia.fechaAdicion.toISOString() : evidencia.fechaAdicion)}
        </div>
      </div>
    </a>
  );
}

// ─── Empresa Detail ──────────────────────────────────

type EmpresaTab = 'ntcs' | 'asesorias' | 'indicadores' | 'progreso' | 'snapshots';

function EmpresaDetail({ data }: { data: DashboardEmpresa }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canImpersonate, impersonate } = useImpersonation();
  const [impersonating, setImpersonating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const activeTab = (location.pathname.split('/').pop() as EmpresaTab) ?? 'ntcs';

  const initials = data.empresa.nombre
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  const handleImpersonate = async () => {
    if (!data.empresa.asesorId) return;
    setImpersonating(true);
    try {
      await impersonate(Number(data.empresa.asesorId));
      window.location.replace(`/empresa/${data.empresa.nit}`);
    } catch {
      /* */
    } finally {
      setImpersonating(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const blob = await dashboardService.downloadEmpresaExcelReport(data.empresa.nit);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_${data.empresa.nombre}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error descargando reporte:', err);
    } finally {
      setDownloading(false);
    }
  };

  const tabs: { id: EmpresaTab; label: string }[] = [
    { id: 'ntcs', label: 'Estado Actual Normas' },
    { id: 'asesorias', label: 'Historial asesorías' },
    { id: 'indicadores', label: 'Indicadores' },
    { id: 'progreso', label: 'Progreso' },
    { id: 'snapshots', label: 'Cronología' },
  ];

  return (
    <>
      <button
        type="button"
        className="flex items-center gap-1.5 text-[12px] text-(--admin-text2) hover:text-(--admin-text) mb-4"
        onClick={() => navigate(rutaListaEmpresas())}
      >
        ← Volver a empresas
      </button>

      {/* Header Panel */}
      <div className="admin-detail-panel">
        <div className="flex items-start gap-4 mb-5">
          <div className="admin-avatar" style={{ background: 'var(--admin-green-bg)', color: 'var(--admin-green)' }}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-semibold text-(--admin-text) mb-0.5">{data.empresa.nombre}</div>
            <div className="text-[12px] text-(--admin-text3)">NIT: {data.empresa.nit}</div>
          </div>
        </div>

        <div className="admin-detail-grid">
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Cumplimiento general</div>
            <div className="text-[13px] font-medium" style={{ color: pColor(data.cumplimientoTotal) }}>
              {data.cumplimientoTotal.toFixed(1)}%
            </div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Asesor asignado</div>
            <div className="text-[13px] font-medium">
              {data.empresa.asesorId ? (
                <button onClick={() => navigate(`/asesores/${data.empresa.asesorId}`)} className="text-(--admin-blue) hover:underline">
                  {data.empresa.asesor}
                </button>
              ) : (
                data.empresa.asesor || '—'
              )}
            </div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Última actividad</div>
            <div className="text-[13px] font-medium">{formatDate(data.ultimaActividad)}</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Horas aprobadas</div>
            <div className="text-[13px] font-medium">{`${formatHours(data.horasTotales)} / 320h (${((data.horasTotales * 100) / 320).toFixed(1)}%)`}</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Total asesorías</div>
            <div className="text-[13px] font-medium">{data.totalAsesorias}</div>
          </div>
        </div>

        {/* NTC Progress Breakdown */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4 bg-(--admin-surface2) rounded-lg border border-(--admin-border)">
          <div className="flex flex-col gap-1">
            <div className="text-[10px] text-(--admin-text3) uppercase font-bold tracking-widest">{data.resumen.nombreN1 ?? 'Norma 1'}</div>
            <ProgBar value={data.resumen.cumplimientoN1} color={pColor(data.resumen.cumplimientoN1)} fill decimals={2} />
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-[10px] text-(--admin-text3) uppercase font-bold tracking-widest">{data.resumen.nombreN2 ?? 'Norma 2'}</div>
            <ProgBar value={data.resumen.cumplimientoN2} color={pColor(data.resumen.cumplimientoN2)} fill decimals={2} />
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-[10px] text-(--admin-text3) uppercase font-bold tracking-widest">{data.resumen.nombreN3 ?? 'Norma 3'}</div>
            <ProgBar value={data.resumen.cumplimientoN3} color={pColor(data.resumen.cumplimientoN3)} fill decimals={2} />
          </div>
        </div>

        <div className="flex gap-2">
          {data.asesoriaActual && (
            <button
              type="button"
              className="admin-btn primary"
              onClick={() => navigate(`/admin/empresa/${data.empresa.nit}/asesoria/${data.asesoriaActual!.asesoriaId}/matriz`)}
            >
              <Maximize className="w-3.5 h-3.5" /> Ver Matriz de Evaluación
            </button>
          )}
          <button
            type="button"
            className="admin-btn bg-(--admin-surface2) text-(--admin-text2) border-(--admin-border) hover:bg-(--admin-surface3) disabled:opacity-50"
            onClick={handleDownloadExcel}
            disabled={downloading}
          >
            <Download className="w-3.5 h-3.5" /> {downloading ? 'Generando...' : 'Exportar Excel'}
          </button>
          {canImpersonate && data.empresa.asesorId && (
            <button
              type="button"
              onClick={handleImpersonate}
              disabled={impersonating}
              className="admin-btn bg-(--admin-amber-bg) text-(--admin-amber) border-transparent hover:opacity-80 disabled:opacity-50"
            >
              <LogIn className="w-3.5 h-3.5" /> {impersonating ? 'Accediendo...' : 'Acceder como asesor'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => navigate(`/empresas/${data.empresa.nit}/${tab.id}`)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Outlet context={data} />
    </>
  );
}

// ─── Proyección de Cumplimiento ──────────────────────

function ProyeccionLayerCardSkeleton() {
  return (
    <div className="admin-card p-4 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="h-16 bg-gray-100 rounded" />
        <div className="h-16 bg-gray-100 rounded" />
      </div>
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 bg-gray-50 rounded" />
        ))}
      </div>
    </div>
  );
}

const PROYECCION_LAYER_TOOLTIPS: Record<string, string> = {
  Aprobado:
    'Cumplimiento oficial actual: solo asesorías finalizadas y aprobadas (EstadoActualRequisito). No incluye ninguna asesoría en revisión.',
  'Con Correcciones':
    'Baseline aprobado + asesorías que requieren correcciones + asesorías en revisión ya marcadas para auto-aprobar. Muestra el impacto de lo que está pendiente de ajuste.',
  Proyectado:
    'Baseline "Con Correcciones" + todas las asesorías en revisión o con correcciones que ya están limpias (sin rechazos ni ítems sin revisar), estén o no marcadas para auto-aprobar.',
};

function ProyeccionLayerCard({ titulo, capa, ntcs }: { titulo: string; capa: ProyeccionCapa; ntcs: DashboardEmpresaNTC[] }) {
  return (
    <div className="admin-card p-4 flex flex-col justify-between h-full">
      <div>
        <div
          className="text-[12px] font-bold text-(--admin-text) uppercase tracking-wider mb-3 cursor-help w-fit"
          title={PROYECCION_LAYER_TOOLTIPS[titulo]}
        >
          {titulo}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="admin-metric-card flex flex-col justify-between p-2">
            <div>
              <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">Cumplimiento global</div>
              <div className="text-xl font-bold mb-1" style={{ color: pColor(capa.cumplimientoGlobal) }}>
                {capa.cumplimientoGlobal.toFixed(1)}%
              </div>
            </div>
            <ProgBar value={capa.cumplimientoGlobal} color={pColor(capa.cumplimientoGlobal)} fill decimals={1} />
          </div>
          <div className="admin-metric-card flex flex-col justify-between p-2">
            <div>
              <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">Horas acumuladas</div>
              <div className="text-[14px] font-bold text-gray-800 mb-1">{capa.horasAcumuladas.toFixed(0)}h / 320h</div>
            </div>
            <ProgBar value={capa.horasPct} color="var(--admin-blue)" fill decimals={1} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {ntcs.map((ntc) => {
            const porNtc = capa.porNtc.find((p) => String(p.ntcId) === String(ntc.ntcId));
            const porcentaje = porNtc?.porcentaje ?? 0;
            const color = pColor(porcentaje);
            return (
              <div
                key={ntc.ntcId}
                className="admin-metric-card flex flex-col gap-1.5 py-2 px-3"
                title={
                  porNtc
                    ? `Cumple: ${porNtc.totalC} | Cumple Parcial: ${porNtc.totalCP} | No Cumple: ${porNtc.totalNC} | No Aplica: ${porNtc.totalNA}`
                    : undefined
                }
              >
                <div className="flex items-center justify-between w-full">
                  <span className="text-[11px] font-medium text-(--admin-text2)">{porNtc?.nombre ?? ntc.nombre}</span>
                  {porNtc && (
                    <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[9px] font-semibold border border-gray-200">
                      C:{porNtc.totalC} CP:{porNtc.totalCP} NC:{porNtc.totalNC} NA:{porNtc.totalNA}
                    </span>
                  )}
                </div>
                <div className="w-full mt-0.5">
                  <span className="inline-flex items-center gap-1.5 w-full">
                    <span className="admin-prog-bar bg-white border border-gray-200 flex-1">
                      <span className="admin-prog-fill" style={{ width: `${porcentaje}%`, background: color }}></span>
                    </span>
                    <span className="text-[11px] font-semibold text-gray-700 whitespace-nowrap">
                      {porNtc ? `${porcentaje.toFixed(1)}%` : '—'}
                    </span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ProyeccionCumplimientoSection({ nit, ntcs }: { nit: string; ntcs: DashboardEmpresaNTC[] }) {
  const { data, isLoading, error } = useProyeccionCumplimiento(nit);

  return (
    <div className="mb-6">
      <h3 className="text-[14px] font-bold text-(--admin-text) mb-3">Proyeccion de Cumplimiento</h3>
      {error && (
        <div className="admin-card p-3 mb-3 text-[12px] text-(--admin-red)" role="alert">
          No se pudo cargar la proyeccion de cumplimiento: {error}
        </div>
      )}
      {isLoading && !data ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ProyeccionLayerCardSkeleton />
          <ProyeccionLayerCardSkeleton />
          <ProyeccionLayerCardSkeleton />
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ProyeccionLayerCard titulo="Aprobado" capa={data.aprobado} ntcs={ntcs} />
          <ProyeccionLayerCard titulo="Con Correcciones" capa={data.conCorrecciones} ntcs={ntcs} />
          <ProyeccionLayerCard titulo="Proyectado" capa={data.proyectado} ntcs={ntcs} />
        </div>
      ) : null}
    </div>
  );
}

// ─── Tab Components ──────────────────────────────────

export function EmpresaNtcsTab() {
  const data = useOutletContext<DashboardEmpresa>();
  const navigate = useNavigate();
  const showSecretPanel = useAdminSecretPanel();
  const [showMatrix, setShowMatrix] = useState(false);
  const [expandEvidencias, setExpandEvidencias] = useState(false);
  const [expandAudios, setExpandAudios] = useState(false);
  const [expandedNtcFormula, setExpandedNtcFormula] = useState<number | null>(null);
  const { data: matrixData, isLoading: loadingMatrix, refetch: refetchMatrix } = useEvaluacionActual(showMatrix ? data.empresa.nit : null);

  return (
    <div className="space-y-4">
      {/* Widget principal de resumen de horas (visible para todos los administradores) */}
      <HorasResumenCard resumen={computeResumenHoras(data.asesorias)} />

      {/* Proyección de cumplimiento (revelada si showSecretPanel es true) */}
      {showSecretPanel && <ProyeccionCumplimientoSection nit={data.empresa.nit} ntcs={data.ntcs} />}
      {data.ntcs.length === 0 ? (
        <div className="admin-card p-8 text-center text-(--admin-text3)">Sin NTCs asignadas</div>
      ) : (
        [...data.ntcs]
          .sort((a, b) => a.orden - b.orden)
          .map((ntc) => {
            const numerador = ntc.totalC + ntc.totalCP * 0.5;
            const denominador = ntc.totalRequisitos - ntc.totalNA;
            const p = denominador > 0 ? (numerador / denominador) * 100 : 0;
            const isExpanded = expandedNtcFormula === ntc.id;

            return (
              <div
                key={ntc.id}
                className="admin-card overflow-hidden cursor-pointer hover:border-(--admin-blue) transition-colors group"
                onClick={() => setExpandedNtcFormula(isExpanded ? null : ntc.id)}
              >
                <div className="flex items-center gap-3 p-3 px-4 bg-(--admin-surface2) border-b border-(--admin-border) group-hover:bg-(--admin-blue-bg) transition-colors">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/normas/${ntc.ntcId}`);
                      }}
                      className="admin-chip bg-white text-(--admin-blue) font-bold border border-(--admin-blue-bg) hover:bg-(--admin-blue) hover:text-white transition-colors"
                    >
                      {ntc.nombre}
                    </button>
                  </div>

                  <div className="ml-auto flex items-center gap-4 text-[11px] font-medium text-(--admin-text2)">
                    <span className="flex items-center gap-1" title="Cumple">
                      <span className="w-2 h-2 rounded-full bg-(--admin-green) inline-block" /> C: {ntc.totalC}
                    </span>
                    <span className="flex items-center gap-1" title="Cumple Parcialmente">
                      <span className="w-2 h-2 rounded-full bg-[#e09b00] inline-block" /> CP: {ntc.totalCP}
                    </span>
                    <span className="flex items-center gap-1" title="No Cumple">
                      <span className="w-2 h-2 rounded-full bg-(--admin-red) inline-block" /> NC: {ntc.totalNC}
                    </span>
                    <span className="flex items-center gap-1" title="No Aplica">
                      <span className="w-2 h-2 rounded-full bg-(--admin-text3) inline-block shadow-sm" /> NA: {ntc.totalNA}
                    </span>
                    <span className="font-black text-[14px] ml-2" style={{ color: pColor(p) }}>
                      {p.toFixed(1)}%
                    </span>
                    <div className="p-1 border-l border-(--admin-border) pl-3">
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-(--admin-blue)" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-(--admin-text3) group-hover:text-(--admin-blue)" />
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-4 bg-white animate-in slide-in-from-top-1 duration-200">
                    <div className="flex flex-col gap-3 max-w-md">
                      <div className="text-[12px] font-bold text-(--admin-text) border-b border-(--admin-border) pb-1 mb-1">
                        Cálculo de Cumplimiento (NTC)
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-(--admin-text3)">1. Numerador (Puntos obtenidos)</span>
                          <span className="font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                            {ntc.totalC} + ({ntc.totalCP} × 0.5) = <span className="font-bold text-(--admin-text)">{numerador}</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-(--admin-text3)">2. Denominador (Puntos posibles)</span>
                          <span className="font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                            {ntc.totalRequisitos} - {ntc.totalNA} = <span className="font-bold text-(--admin-text)">{denominador}</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[11px] pt-1 border-t border-dashed border-(--admin-border)">
                          <span className="font-bold text-(--admin-text)">3. Resultado Final</span>
                          <span className="font-mono bg-(--admin-blue-bg) text-(--admin-blue) px-1.5 py-0.5 rounded border border-(--admin-blue-bg)">
                            ({numerador} / {denominador}) × 100 = <span className="font-bold">{p.toFixed(2)}%</span>
                          </span>
                        </div>
                      </div>
                      <p className="text-[10px] text-(--admin-text3) italic mt-1 leading-relaxed">
                        * La fórmula excluye los requisitos que "No Aplican" (NA) del denominador. Los cumplimientos parciales (CP) suman el
                        50% de un punto.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
      )}

      <div className="flex flex-col gap-4 mt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] font-bold text-(--admin-text)">Auditoría detallada de requisitos</h3>
          <button
            type="button"
            onClick={() => setShowMatrix(!showMatrix)}
            className={`admin-btn flex items-center gap-2 ${showMatrix ? 'bg-(--admin-blue) text-white border-transparent' : ''}`}
          >
            <LayoutList className="w-4 h-4" />
            {showMatrix ? 'Cerrar matriz actual' : 'Ver matriz actual (3 columnas)'}
          </button>
        </div>

        {showMatrix && (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-300 min-h-[70vh]">
            {loadingMatrix ? (
              <MatrizSkeleton />
            ) : matrixData ? (
              <MatrizInline data={matrixData} onRefresh={refetchMatrix} />
            ) : (
              <div className="p-12 text-center bg-(--admin-surface2) rounded-xl border border-(--admin-border)">
                <p className="text-sm text-(--admin-text3)">No se pudo cargar la matriz de evaluación actual.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Evidencias y Audios Recientes */}
      {data.asesoriaActual && (data.asesoriaActual.evidencias?.length || data.asesoriaActual.audios?.length) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Evidencias */}
          <div className="admin-card overflow-hidden">
            <button
              type="button"
              onClick={() => setExpandEvidencias(!expandEvidencias)}
              className="flex items-center justify-between w-full p-4 hover:bg-(--admin-surface2) transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-(--admin-green)" />
                <h3 className="text-[14px] font-bold text-(--admin-text)">
                  Evidencias ({data.asesoriaActiva ? 'Asesoría Actual' : 'Última Asesoría'})
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="admin-chip text-[10px]" style={{ background: 'var(--admin-green-bg)', color: 'var(--admin-green)' }}>
                  {data.asesoriaActual.evidencias?.length ?? 0}
                </span>
                {expandEvidencias ? (
                  <ChevronUp className="w-4 h-4 text-(--admin-text3)" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-(--admin-text3)" />
                )}
              </div>
            </button>
            {expandEvidencias && (
              <div className="p-5 pt-0">
                {data.asesoriaActual.evidencias?.length ? (
                  <div className="flex flex-col">
                    {data.asesoriaActual.evidencias.map((ev) => (
                      <EvidenciaItem key={ev.evidenciaId} evidencia={ev} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-[12px] text-(--admin-text3)">Sin evidencias recientes</div>
                )}
              </div>
            )}
          </div>

          {/* Audios */}
          <div className="admin-card overflow-hidden">
            <button
              type="button"
              onClick={() => setExpandAudios(!expandAudios)}
              className="flex items-center justify-between w-full p-4 hover:bg-(--admin-surface2) transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileAudio className="w-4 h-4 text-(--admin-purple)" />
                <h3 className="text-[14px] font-bold text-(--admin-text)">
                  Audios ({data.asesoriaActiva ? 'Asesoría Actual' : 'Última Asesoría'})
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="admin-chip text-[10px]" style={{ background: 'var(--admin-purple-bg)', color: 'var(--admin-purple)' }}>
                  {data.asesoriaActual.audios?.length ?? 0}
                </span>
                {expandAudios ? (
                  <ChevronUp className="w-4 h-4 text-(--admin-text3)" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-(--admin-text3)" />
                )}
              </div>
            </button>
            {expandAudios && (
              <div className="p-5 pt-0">
                {data.asesoriaActual.audios?.length ? (
                  <div className="flex flex-col">
                    {data.asesoriaActual.audios.map((au) => (
                      <AudioItem key={au.audioId} audio={au} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-[12px] text-(--admin-text3)">Sin audios recientes</div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function EmpresaAsesoriasTab() {
  const data = useOutletContext<DashboardEmpresa>();
  return <AsesoriaHistorialTable asesorias={data.asesorias} nitEmpresa={data.empresa.nit} />;
}

export function EmpresaIndicadoresTab() {
  const data = useOutletContext<DashboardEmpresa>();
  return (
    <div className="space-y-6">
      <CompanyIndicatorsCharts nit={data.empresa.nit} />
      <EmpresaIndicadoresPanel nit={data.empresa.nit} readOnly={true} />
    </div>
  );
}

export function EmpresaProgresoTab() {
  const data = useOutletContext<DashboardEmpresa>();
  return <ProgresoEmpresa nit={data.empresa.nit} />;
}

export function EmpresaSnapshotsTab() {
  const data = useOutletContext<DashboardEmpresa>();
  return <SnapshotsView nit={data.empresa.nit} />;
}

// ─── Main Page ─────────────────────────────────────

/**
 * Filtros de la lista guardados en la URL (?q=&ntc=&asesor=&estado=&agrupar=&orden=) para poder
 * compartir un enlace con la lista ya filtrada. En `q` se pueden poner varios nombres o NIT separados
 * por coma: sale cualquier empresa que coincida con alguno.
 */
function useFiltroUrl<T extends string>(clave: string, porDefecto: T): [T, (v: T) => void] {
  const [params, setParams] = useSearchParams();
  const valor = (params.get(clave) ?? porDefecto) as T;
  const cambiar = (v: T) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (!v || v === porDefecto) p.delete(clave);
        else p.set(clave, v);
        return p;
      },
      { replace: true }
    );
  return [valor, cambiar];
}

/**
 * Lista de NIT compacta para la URL (?nits=): cada NIT en base 36, separados por punto. Acorta el
 * enlace ~30 % frente a los NIT en decimal y compara el NIT exacto (no por coincidencia parcial).
 */
const codificarNits = (nits: string[]) => nits.map((n) => Number(n).toString(36)).join('.');
const decodificarNits = (v: string) =>
  v
    .split('.')
    .map((x) => parseInt(x, 36))
    .filter((n) => Number.isSafeInteger(n) && n > 0)
    .map(String);

/** Términos de búsqueda: separados por coma, punto y coma o salto de línea. */
const terminosBusqueda = (q: string) =>
  q
    .split(/[,;\n]/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

export function AdminEmpresasPage() {
  const { nit: nitParam } = useParams<{ nit: string }>();
  const navigate = useNavigate();
  const { empresas, isLoading, error } = useDashboardEmpresas();
  const { asesores } = useDashboardAsesores();
  const [search, setSearch] = useFiltroUrl<string>('q', '');
  const [ntcFilter, setNtcFilter] = useFiltroUrl<string>('ntc', '');
  const [asesorFilter, setAsesorFilter] = useFiltroUrl<string>('asesor', '');
  const [estadoFilter, setEstadoFilter] = useFiltroUrl<'todos' | 'activa' | 'sin_activa'>('estado', 'todos');
  const [groupBy, setGroupBy] = useFiltroUrl<'none' | 'asesor' | 'ntc'>('agrupar', 'none');
  const [sortBy, setSortBy] = useFiltroUrl<'nombre' | 'cumplimiento' | 'horas'>('orden', 'nombre');
  const [nitsParam, setNitsParam] = useFiltroUrl<string>('nits', '');
  const listaNits = useMemo(() => new Set(decodificarNits(nitsParam)), [nitsParam]);
  const [copiado, setCopiado] = useState(false);
  const { search: queryActual } = useLocation();

  // En la lista (no en el detalle) se guarda el filtro actual; "Volver a empresas" lo recupera.
  useEffect(() => {
    if (nitParam) return;
    try {
      sessionStorage.setItem(CLAVE_FILTROS_EMPRESAS, queryActual);
    } catch {
      // Sin sessionStorage (modo privado): se vuelve a la lista sin filtros.
    }
  }, [nitParam, queryActual]);

  const selectedNit = nitParam ?? null;
  const { data: detalle, isLoading: loadingDetalle } = useDashboardEmpresa(selectedNit ?? '');

  const asesorMap = useMemo(() => new Map(asesores.map((a) => [a.asesorId, a.nombre])), [asesores]);

  const uniqueNTCs = useMemo(() => {
    const s = new Set(empresas.map((e) => e.combinacionNtc?.nombre).filter(Boolean));
    return Array.from(s).sort();
  }, [empresas]);

  const filtered = useMemo(() => {
    const terminos = terminosBusqueda(search);
    return empresas.filter((e) => {
      if (listaNits.size > 0 && !listaNits.has(e.nitId)) return false;
      const matchSearch =
        terminos.length === 0 || terminos.some((t) => e.nombre?.toLowerCase().includes(t) || e.nitId?.toLowerCase().includes(t));
      const matchNTC = !ntcFilter || e.combinacionNtc?.nombre === ntcFilter;
      const matchAsesor = !asesorFilter || e.asesorId.toString() === asesorFilter;
      const matchEstado =
        estadoFilter === 'todos' ||
        (estadoFilter === 'activa' && !!e.asesoriaActivaId) ||
        (estadoFilter === 'sin_activa' && !e.asesoriaActivaId);
      return matchSearch && matchNTC && matchAsesor && matchEstado;
    });
  }, [empresas, search, ntcFilter, asesorFilter, estadoFilter, listaNits]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sortBy === 'cumplimiento') {
        return (b.porcentajeCumplimiento ?? 0) - (a.porcentajeCumplimiento ?? 0);
      }
      if (sortBy === 'horas') {
        return (b.horasAsesoria ?? 0) - (a.horasAsesoria ?? 0);
      }
      return (a.nombre ?? '').localeCompare(b.nombre ?? '');
    });
  }, [filtered, sortBy]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map<string, { emps: typeof sorted; avg: number }>();
    sorted.forEach((e) => {
      let key = 'Sin clasificar';
      if (groupBy === 'asesor') key = asesorMap.get(e.asesorId) || 'Asesor desconocido';
      if (groupBy === 'ntc') key = e.combinacionNtc?.nombre || 'Sin NTC';

      if (!map.has(key)) map.set(key, { emps: [], avg: 0 });
      map.get(key)!.emps.push(e);
    });

    // Calcular promedios
    map.forEach((val, groupName) => {
      let sum = 0;
      if (groupBy === 'ntc') {
        // Promedio específico de la norma por la que se agrupa
        sum = val.emps.reduce((s, e) => {
          const ntcComp = e.cumplimientoNtcs?.find((c: any) => c.ntc.nombre === groupName);
          return s + (ntcComp?.porcentajeCumplimiento ?? e.porcentajeCumplimiento ?? 0);
        }, 0);
      } else {
        // Promedio general
        sum = val.emps.reduce((s, e) => s + (e.porcentajeCumplimiento ?? 0), 0);
      }
      val.avg = sum / (val.emps.length || 1);
    });

    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sorted, groupBy, asesorMap]);

  const {
    visible: colVisible,
    toggle: toggleCol,
    reset: resetCols,
    activeCount: colCount,
  } = useColumnVisibility<EmpresaColKey>(EMPRESA_COLS, 'tourixia:admin-empresas-cols');

  // Reset filters when navigating back to list
  // (no need to reset selectedNit since it comes from URL)

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center text-(--admin-red)">
        <p className="font-medium">{error}</p>
      </div>
    );
  }

  // Show detail view
  if (selectedNit && detalle) {
    return <EmpresaDetail data={detalle} />;
  }

  if (selectedNit && loadingDetalle) {
    return (
      <>
        <button
          type="button"
          className="flex items-center gap-1.5 text-[12px] text-(--admin-text2) hover:text-(--admin-text) mb-4"
          onClick={() => navigate(rutaListaEmpresas())}
        >
          ← Volver a empresas
        </button>
        <div className="p-12 text-center">
          <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Metrics */}
      <div className="admin-metrics-row mb-6">
        <div className="admin-metric-card">
          <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">Total Empresas</div>
          <div className="text-2xl font-bold">{empresas.length}</div>
          <div className="text-[10px] text-(--admin-text3) mt-1">Registradas en sistema</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">En Asesoría</div>
          <div className="text-2xl font-bold text-(--admin-green)">{empresas.filter((e) => e.asesoriaActivaId).length}</div>
          <div className="text-[10px] text-(--admin-text3) mt-1">Con proceso activo</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">Promedio Cumpl.</div>
          <div className="text-2xl font-bold text-(--admin-blue)">
            {(empresas.reduce((s, e) => s + (e.porcentajeCumplimiento ?? 0), 0) / (empresas.length || 1)).toFixed(1)}%
          </div>
          <div className="text-[10px] text-(--admin-text3) mt-1">Global de cumplimiento</div>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-(--admin-surface) border border-(--admin-border) rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="flex-1 min-w-60">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Búsqueda</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--admin-text3)" />
              <input
                type="text"
                placeholder="Nombre o NIT (varios separados por coma)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-search-input w-full pl-9"
              />
            </div>
            {listaNits.size > 0 && (
              <div className="mt-1 flex items-center gap-2 text-[11px] text-(--admin-text2)">
                Lista del enlace: {listaNits.size} empresa(s)
                <button type="button" onClick={() => setNitsParam('')} className="text-(--admin-accent,#2563eb) hover:underline">
                  Quitar
                </button>
              </div>
            )}
            {terminosBusqueda(search).length > 1 && (
              <div className="mt-1 text-[11px] text-(--admin-text3)">
                {terminosBusqueda(search).length} términos · {filtered.length} empresa(s)
              </div>
            )}
          </div>

          {/* NTC Filter */}
          <div className="w-44">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Norma NTC</label>
            <select value={ntcFilter} onChange={(e) => setNtcFilter(e.target.value)} className="admin-select w-full">
              <option value="">Todas</option>
              {uniqueNTCs.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          {/* Asesor Filter */}
          <div className="w-44">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Asesor</label>
            <select value={asesorFilter} onChange={(e) => setAsesorFilter(e.target.value)} className="admin-select w-full">
              <option value="">Todos</option>
              {asesores.map((a) => (
                <option key={a.asesorId} value={a.asesorId}>
                  {a.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Estado Filter */}
          <div className="w-44">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Estado</label>
            <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value as any)} className="admin-select w-full">
              <option value="todos">Cualquier estado</option>
              <option value="activa">En asesoría</option>
              <option value="sin_activa">Sin asesoría</option>
            </select>
          </div>

          {/* Enlace con los filtros actuales */}
          <button
            type="button"
            onClick={() => {
              // Si la búsqueda es solo una lista de NIT, el enlace la lleva compacta en ?nits=.
              const terminos = terminosBusqueda(search);
              const url = new URL(window.location.href);
              if (terminos.length > 1 && terminos.every((t) => /^\d+$/.test(t))) {
                const todos = [...new Set([...listaNits, ...terminos])];
                url.searchParams.delete('q');
                url.searchParams.set('nits', codificarNits(todos));
              }
              void navigator.clipboard?.writeText(url.toString()).then(() => {
                setCopiado(true);
                setTimeout(() => setCopiado(false), 2000);
              });
            }}
            className="admin-select flex items-center gap-1.5 text-[12px]"
            title="Copia el enlace con estos filtros para compartirlo"
          >
            <Link2 className="w-3.5 h-3.5" />
            {copiado ? 'Copiado' : 'Copiar enlace'}
          </button>

          {/* Sort & Group */}
          <div className="flex gap-2 border-l border-(--admin-border) pl-4 ml-auto">
            <div>
              <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Agrupar por</label>
              <div className="flex bg-(--admin-surface2) p-0.5 rounded-lg border border-(--admin-border)">
                <button
                  onClick={() => setGroupBy('none')}
                  className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'none' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
                >
                  Nada
                </button>
                <button
                  onClick={() => setGroupBy('asesor')}
                  className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'asesor' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
                >
                  Asesor
                </button>
                <button
                  onClick={() => setGroupBy('ntc')}
                  className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'ntc' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
                >
                  Norma
                </button>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Ordenar por</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="admin-select">
                <option value="nombre">Nombre (A-Z)</option>
                <option value="cumplimiento">Cumplimiento (%)</option>
                <option value="horas">Horas de Asesoría</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="admin-card">
        <div className="admin-card-header flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="text-[13px] font-semibold text-(--admin-text) flex items-center gap-2">
              Empresas
              <span className="bg-(--admin-surface2) px-1.5 py-0.5 rounded text-(--admin-text3) font-normal text-[11px] border border-(--admin-border)">
                {sorted.length}
              </span>
            </div>
          </div>
          <ColumnPicker cols={EMPRESA_COLS} visible={colVisible} onToggle={toggleCol} onReset={resetCols} activeCount={colCount} />
        </div>
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                {EMPRESA_COLS.filter((c) => colVisible[c.key]).map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groupBy === 'none'
                ? sorted.map((e) => renderEmpresaRow(e, colVisible, navigate, asesorMap))
                : groups?.map(([groupName, groupData]) => (
                    <Fragment key={groupName}>
                      <tr className="bg-(--admin-bg) border-y border-(--admin-border)">
                        <td colSpan={EMPRESA_COLS.filter((c) => colVisible[c.key]).length + 1} className="py-2.5 px-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-bold text-(--admin-text2) uppercase tracking-widest">{groupName}</span>
                              <span className="text-[10px] text-(--admin-text3) bg-white px-1.5 py-0.5 rounded border border-(--admin-border)">
                                {groupData.emps.length}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-tighter">Promedio:</span>
                              <span className="text-[14px] font-bold" style={{ color: pColor(groupData.avg) }}>
                                {groupData.avg.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {groupData.emps.map((e) => renderEmpresaRow(e, colVisible, navigate, asesorMap))}
                    </Fragment>
                  ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={EMPRESA_COLS.filter((c) => colVisible[c.key]).length + 1} className="text-center py-12 text-(--admin-text3)">
                    <Building2 className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <div className="text-[14px] font-medium">No se encontraron empresas</div>
                    <div className="text-[12px] opacity-70">Intenta ajustar los filtros de búsqueda</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── Render Helper ───────────────────────────────────

function renderEmpresaRow(e: any, visible: any, navigate: any, asesorMap: Map<any, string>) {
  const p = e.porcentajeCumplimiento ?? 0;
  return (
    <tr key={e.nitId} className="group hover:bg-[#faf9f7]! clickable" onClick={() => navigate(`/empresas/${e.nitId}`)}>
      {visible.nombre && <td className="font-medium text-(--admin-text) max-w-64 truncate">{e.nombre}</td>}
      {visible.nit && <td className="font-mono text-[11px] text-(--admin-text3) whitespace-nowrap">{e.nitId}</td>}
      {visible.ntc && (
        <td className="whitespace-nowrap">
          <div className="flex flex-col gap-1">
            {e.cumplimientoNtcs?.length > 0 ? (
              e.cumplimientoNtcs.map((c: any) => (
                <div key={c.ntcId} className="flex items-center gap-1.5">
                  <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue) text-[9px] max-w-32 truncate border-transparent">
                    {c.ntc.nombre}
                  </span>
                  <span className="text-[10px] font-bold" style={{ color: pColor(c.porcentajeCumplimiento) }}>
                    {c.porcentajeCumplimiento.toFixed(1)}%
                  </span>
                </div>
              ))
            ) : (
              <span className="admin-chip bg-(--admin-surface2) text-(--admin-text2) text-[10px]">{e.combinacionNtc?.nombre ?? '—'}</span>
            )}
          </div>
        </td>
      )}
      {visible.asesor && <td className="text-(--admin-text2) whitespace-nowrap text-[12px]">{asesorMap.get(e.asesorId) ?? '—'}</td>}
      {visible.cumplimiento && (
        <td className="whitespace-nowrap">
          <ProgBar value={p} color={pColor(p)} />
        </td>
      )}
      {visible.horas && (
        <td className="whitespace-nowrap">
          <div className="flex flex-col gap-0.5">
            <ProgBar value={(e.horasAsesoria * 100) / 320} color="var(--admin-blue)" decimals={1} />
            <span className="text-[10px] text-(--admin-text3) font-medium">{formatHours(e.horasAsesoria)} / 320h</span>
          </div>
        </td>
      )}
      {visible.asesoria && (
        <td className="whitespace-nowrap">
          {e.asesoriaActivaId ? (
            <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green) border-(--admin-green-bg) animate-pulse!">En curso</span>
          ) : (
            <span className="admin-chip bg-(--admin-surface2) text-(--admin-text2) border-(--admin-border)">Sin activa</span>
          )}
        </td>
      )}
      <td className="text-right whitespace-nowrap">
        <button
          className="admin-btn text-[11px] py-1 px-3 bg-white border-(--admin-border) opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(ev) => {
            ev.stopPropagation();
            navigate(`/empresas/${e.nitId}`);
          }}
        >
          Ver detalle
        </button>
      </td>
    </tr>
  );
}
